import { Box, Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { isJobRunning, Job } from '~/state/atoms/jobAtoms';
import { autoLoadResultsState } from '~/state/atoms/viewerAtoms';
import { JobDetails } from './JobDetails';
import { JobPhase } from './JobPhase';
import { JobStatusIcon } from './JobStatusIcon';

interface JobItemProps {
    job: Job;
    onClear: (job: Job) => void;
    onCancel: (job: Job) => void;
    onRetryDownload: (job: Job) => void;
}

const JobItem = ({ job, onClear, onCancel, onRetryDownload }: JobItemProps) => {
    const intl = useIntl();
    const [hovered, setHovered] = useState(false);
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const autoLoad = useRecoilValue(autoLoadResultsState);

    useEffect(() => {
        if (
            autoLoad &&
            job.status === 'Done' &&
            job.results &&
            !job.isImported
        ) {
            job.onLoad?.(job);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only care about auto-loading at the exact moment a job finishes
    }, [job.status]);

    return (
        <Box
            ref={anchorRef}
            display="flex"
            gap={1.5}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            sx={(theme) => ({
                p: 1.5,
                borderRadius: 1,
                backgroundColor: theme.palette.background.paper,
                transition: 'background-color 0.2s',
            })}
        >
            <Box mt={0.5} width={20}>
                <JobStatusIcon status={job.status} />
            </Box>
            <Box display="grid" width="100%">
                <JobDetails job={job} anchorEl={anchorRef.current} />
                <JobPhase
                    job={job}
                    onClear={onClear}
                    onRetryDownload={onRetryDownload}
                    anchorEl={anchorRef.current}
                />
            </Box>
            <Box position="relative">
                {hovered && (
                    <Tooltip
                        title={isJobRunning(job.status) ? 'Cancel' : 'Clear'}
                        placement="bottom"
                    >
                        <IconButton
                            size="small"
                            onClick={() => {
                                if (isJobRunning(job.status)) {
                                    onCancel(job);
                                } else {
                                    onClear(job);
                                }
                            }}
                            aria-label={intl.formatMessage({ id: 'clearJob' })}
                            sx={{
                                fontSize: 11,
                                width: 24,
                                height: 24,
                                position: 'absolute',
                                top: -4,
                                right: -4,
                            }}
                        >
                            <X />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
};

export default JobItem;
